import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { siteConfig } from "../config/siteConfig";
import {
  Factory,
  ShieldCheck,
  Sliders,
  MessageCircle,
  Phone,
  Mail,
  PackageCheck,
  ArrowRight,
} from "lucide-react";

const ForYarnManufacturers = () => {
  const { lPath } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "For Yarn Manufacturers",
        "item": "https://skwoolens.trivanahospitality.com/en/for-yarn-manufacturers"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Raw Wool Supply for Yarn Manufacturers",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      }
    },
    "description": "Specification-based raw wool supply for commercial yarn spinners and carpet manufacturers across India. Sourcing 30+ & 32-34+ micron wool in greasy and scoured bales.",
    "areaServed": "India"
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Raw Wool Supplier for Yarn & Carpet Manufacturers | S.K. Woollens"
        description="We supply specification-graded raw wool (30+ and 32-34+ micron) to commercial yarn manufacturers, carpet spinners, and textile mills across India."
        keywords="raw wool for yarn manufacturers, carpet yarn wool supplier, wool supply for spinning mills, 30 micron wool, 32 micron wool, Bikaner wool supply"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/for-yarn-manufacturers"
        schema={[breadcrumbSchema, serviceSchema]}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Factory className="w-3.5 h-3.5 text-wool-bronze" />
              <span>For Yarn Spinners & Mills</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              Raw Wool Sourcing & Bulk Supply for Yarn Manufacturers
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              We specialize in reliable, specification-graded raw wool supply for commercial yarn spinners, focusing on high-resilience 30+ and 32–34+ micron wools for carpet and heavy industrial yarn applications.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Core Sourcing Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Sliders className="w-6 h-6 text-wool-bronze" />
            </div>
            <h2 className="text-lg font-bold text-wool-charcoal">Specification-Based Procurement</h2>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              We align our procurement directly with your carding and spinning parameters — micron, staple length, yield, and VM tolerance.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <PackageCheck className="w-6 h-6 text-wool-bronze" />
            </div>
            <h2 className="text-lg font-bold text-wool-charcoal">Dependable Commercial Batches</h2>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Consistent lot grading and reliable volume supply to keep your spinning mill operating without raw material bottlenecks.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <ShieldCheck className="w-6 h-6 text-wool-bronze" />
            </div>
            <h2 className="text-lg font-bold text-wool-charcoal">Since 1965 Industry Trust</h2>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Backed by decades of wool trade knowledge and deep roots in Bikaner, Rajasthan, India's foremost wool market.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Related Capabilities Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h2 className="text-xl font-bold text-wool-charcoal font-display">
            Recommended Solutions for Mill Buyers
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h3 className="text-sm font-bold text-wool-charcoal">Carpet Wool Supplier</h3>
                <p className="text-xs text-wool-muted mt-1">30+ & 32–34+ micron coarse fleece for carpet yarn spinning.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Explore carpet grades <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-specifications")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h3 className="text-sm font-bold text-wool-charcoal">Wool Specifications</h3>
                <p className="text-xs text-wool-muted mt-1">Detailed technical matrix for carding and spinning.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View matrix <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h3 className="text-sm font-bold text-wool-charcoal">Bulk Mill Contracts</h3>
                <p className="text-xs text-wool-muted mt-1">Multi-tonne recurring monthly deliveries and buffer storage.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View bulk supply <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h3 className="text-sm font-bold text-wool-charcoal">Bikaner Mandi Hub</h3>
                <p className="text-xs text-wool-muted mt-1">Direct mandi procurement at B-26, Un Mandi, Bikaner.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Explore Bikaner hub <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Direct Contact Desk */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-12 shadow-premium">
          <div className="max-w-3xl mb-8 space-y-2">
            <div className="wool-badge">
              <MessageCircle className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Direct Business Contact</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Connect with the S.K. Woollens Supply Desk
            </h2>
            <p className="text-xs sm:text-sm text-wool-muted">
              Share your technical wool requirement directly with the business team via WhatsApp or phone for prompt commercial discussion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-2xl text-emerald-800 font-semibold transition"
            >
              <MessageCircle className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>WhatsApp: +91 7014581250</span>
            </a>

            <a
              href={`tel:${siteConfig.contacts.primaryPhone}`}
              className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition"
            >
              <Phone className="w-5 h-5 text-wool-bronze shrink-0" />
              <span>Call: +91 {siteConfig.contacts.primaryPhone}</span>
            </a>

            <a
              href={`mailto:${siteConfig.contacts.primaryEmail}`}
              className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition md:col-span-2"
            >
              <Mail className="w-5 h-5 text-wool-bronze shrink-0" />
              <span>Email: {siteConfig.contacts.primaryEmail}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForYarnManufacturers;
